/**
 * NebulaVolume — a baked nebula drawn by a ray march, in TSL (space realism, 2026-10-07).
 *
 * The mesh is a unit sphere (scale it to the nebula's radius, put it where the nebula is); its BACK faces are drawn, so
 * the camera may stand outside or inside. Each fragment marches its view ray through the sphere in the mesh's LOCAL
 * space and integrates NebulaShade's arithmetic over the volume NebulaField baked — `marchNebulaCpu` is the oracle of
 * this graph, term for term.
 *
 *   - THE RAY IS BUILT FROM THE FRAGMENT, not from the camera: the fragment is the ray's exit point on the unit sphere,
 *     so the chord and every sample point are O(1) numbers. Solving the camera's quadratic instead cancels in float32
 *     once the camera is some hundred radii away (the planet atmosphere's white-blob bug, 2026-09-09).
 *   - Emission ADDS and dust DIMS in one pass: blending is src + dst·(1 − α) with α = 1 − the mean transmittance and the
 *     colour NOT multiplied by α (three's `premultipliedAlpha` flag would multiply it — THE ONE PREMULTIPLY — and a glow
 *     in front of nothing would vanish). Inside the volume the transmittance is per channel, so what glows behind a
 *     dust lane is reddened; the sky behind the whole nebula takes the mean.
 *   - The start of every ray is dithered by interleaved gradient noise (still: no per-frame offset, so nothing boils
 *     without a temporal resolve).
 *   - `detail`: a small tiling noise volume warps each lookup and erodes the gas — wisps finer than the baked grid.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, vec4, positionLocal, cameraPosition, modelWorldMatrixInverse, Loop, exp, dot, sqrt, max, normalize,
  pow, abs, length, texture3D, screenCoordinate, fract, select, smoothstep, sign, cross,
} from 'three/tsl';
import { markMrtOverlay } from '../../post/mrtOverlay.js';
import { type NebulaSpec, type NebulaVolumeData, NEBULA_PERMS, nebulaVolumeToHalf, bakeNebulaDetail } from './NebulaField.js';
import { NEBULA_STAR_SOFT, NEBULA_STAR_SIGMA, NEBULA_STAR_HALO, NEBULA_STAR_NEAR, NEBULA_SHEET_PX, NEBULA_SHEET_COS_MIN } from './NebulaShade.js';

type N = any;

export interface NebulaQuality {
  /** The baked volume's edge (voxels). */
  size: number;
  /** March steps through the sphere. */
  steps: number;
  /** Warp + erode by the detail volume. */
  detail: boolean;
}
/** low: every tier can afford it (a small volume, a short march); high / ultra: the look the references are matched at. */
export const NEBULA_QUALITY: Record<'low' | 'high' | 'ultra', NebulaQuality> = {
  low: { size: 48, steps: 28, detail: false },
  high: { size: 96, steps: 56, detail: true },
  ultra: { size: 128, steps: 72, detail: true },
};

/** The march's step count for a spec: thin sheets want finer steps (their wrinkles must be near-linear across one). */
export function nebulaSteps(spec: NebulaSpec, quality: NebulaQuality): number {
  return Math.max(6, Math.min(96, Math.round(quality.steps * (spec.sheets.length ? 1.4 : 1))));
}

let detailTex: THREE.Data3DTexture | null = null;
/** The shared tiling detail volume (baked once, ~20 ms). */
export function nebulaDetailTexture(): THREE.Data3DTexture {
  if (detailTex) return detailTex;
  const n = 32;
  const t = new THREE.Data3DTexture(bakeNebulaDetail(n), n, n, n);
  t.format = THREE.RGBAFormat; t.type = THREE.UnsignedByteType;
  t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.wrapS = t.wrapT = t.wrapR = THREE.RepeatWrapping;
  t.colorSpace = THREE.NoColorSpace; t.generateMipmaps = false; t.needsUpdate = true; t.name = 'nebula-detail';
  return (detailTex = t);
}

/** A baked volume as a half-float 3D texture. */
export function nebulaVolumeTexture(v: NebulaVolumeData, name = 'nebula-volume'): THREE.Data3DTexture {
  const t = new THREE.Data3DTexture(nebulaVolumeToHalf(v), v.size, v.size, v.size);
  t.format = THREE.RGBAFormat; t.type = THREE.HalfFloatType;
  t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.wrapS = t.wrapT = t.wrapR = THREE.ClampToEdgeWrapping;
  t.colorSpace = THREE.NoColorSpace; t.generateMipmaps = false; t.needsUpdate = true; t.name = name;
  return t;
}

const v3 = (a: readonly number[]): THREE.Vector3 => new THREE.Vector3(a[0], a[1], a[2]);

/** One nebula: its texture, material and mesh. The caller owns placement (position, scale = the radius). */
export class NebulaVolume {
  readonly mesh: THREE.Mesh;
  readonly material: THREE.NodeMaterial;
  readonly texture: THREE.Data3DTexture;
  readonly quality: NebulaQuality;
  /** The march's step count (the oracle is pinned at the same). */
  readonly steps: number;
  spec: NebulaSpec;
  private readonly U: Record<string, { value: any }>;
  private readonly sheetU: Array<Record<string, { value: any }>>;
  private readonly starU: Array<Record<string, { value: any }>>;

  constructor(spec: NebulaSpec, volume: NebulaVolumeData, quality: NebulaQuality) {
    this.spec = spec;
    this.quality = quality;
    this.texture = nebulaVolumeTexture(volume, `nebula-volume:${spec.kind}:${spec.seed}`);
    const U = this.U = {
      colLow: uniform(new THREE.Vector3()), colHigh: uniform(new THREE.Vector3()), emission: uniform(1), hiR0: uniform(0), hiR1: uniform(1), hiPow: uniform(1),
      scat: uniform(new THREE.Vector3()), phaseG: uniform(0.5), lightPow: uniform(1), ambient: uniform(new THREE.Vector3()),
      kappa: uniform(new THREE.Vector3()), gasKappa: uniform(0), lightPos: uniform(new THREE.Vector3()),
      intensity: uniform(1), fade: uniform(1), pixelAngle: uniform(0.001), detail: uniform(0), detailFreq: uniform(5), jitter: uniform(1), wisps: uniform(1),
    } as Record<string, { value: any }>;
    this.sheetU = spec.sheets.map(() => ({ axesInv: uniform(new THREE.Vector3(1, 1, 1)), radius: uniform(0.5), amp: uniform(0), width: uniform(0.01), split: uniform(0), colOut: uniform(new THREE.Vector3()), colIn: uniform(new THREE.Vector3()), maskLo: uniform(0), maskHi: uniform(0), ripple: uniform(0), sharpen: uniform(0) }));
    this.starU = spec.stars.map(() => ({ pos: uniform(new THREE.Vector3()), col: uniform(new THREE.Vector3()) }));
    this.setSpec(spec);

    const material = new THREE.MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthWrite = false;
    material.side = THREE.BackSide;
    material.fog = false;
    // src + dst·(1 − α), the colour NOT multiplied by α (see the header)
    material.blending = THREE.CustomBlending;
    material.blendEquation = THREE.AddEquation;
    material.blendSrc = THREE.OneFactor; material.blendDst = THREE.OneMinusSrcAlphaFactor;
    material.blendSrcAlpha = THREE.OneFactor; material.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
    markMrtOverlay(material);   // THE OVERLAY LAW: a transparent layer must not rewrite the surface targets behind it

    const steps = this.steps = nebulaSteps(spec, quality);
    const lightMode = spec.light?.mode ?? 'none';
    const sheets = spec.sheets, stars = spec.stars, sheetU = this.sheetU, starU = this.starU;
    const volTex = this.texture, useDetail = quality.detail, dTex = useDetail ? nebulaDetailTexture() : null;
    const comp = (p: N, i: number): N => (i === 0 ? p.x : i === 1 ? p.y : p.z);
    const erfN = (x: N): N => {
      const a = abs(x);
      const d = float(1).add(a.mul(float(0.278393).add(a.mul(float(0.230389).add(a.mul(float(0.000972).add(a.mul(0.078108))))))));
      const d2 = d.mul(d);
      return sign(x).mul(float(1).sub(float(1).div(d2.mul(d2))));
    };
    const sheetInt = (f0: N, f1: N, w: N, dt: N): N => {
      const d = f1.sub(f0), flat = abs(d).lessThan(w.mul(1e-4));
      const m = f0.add(f1).mul(0.5).div(w);
      return select(flat, dt.mul(exp(m.mul(m).negate())), dt.mul(w.mul(0.886226925452758)).mul(erfN(f1.div(w)).sub(erfN(f0.div(w)))).div(select(flat, float(1), d)));
    };
    // `fine`: the detail volume at p (one fetch serves every sheet — each reads its own channel)
    const sheetF = (k: number, p: N, fine: N | null): N => {
      const pm = NEBULA_PERMS[sheets[k].perm % NEBULA_PERMS.length], su = sheetU[k];
      const q = vec3(comp(p, pm[0]).mul(pm[3]), comp(p, pm[1]).mul(pm[4]), comp(p, pm[2]).mul(pm[5]));
      const h = texture3D(volTex, q.mul(0.5).add(0.5)).a;
      const f = length(p.mul(su.axesInv as N)).sub(su.radius as N).sub((su.amp as N).mul(h));
      return fine ? f.sub((su.ripple as N).mul(comp(fine, k % 3).sub(0.5)).mul(U.wisps as N)) : f;
    };
    const fineAt = (p: N): N | null => (dTex && sheets.some((s) => (s.ripple ?? 0) > 0) ? texture3D(dTex, p.mul(U.detailFreq as N)).rgb.toVar() : null);

    material.colorNode = Fn(() => {
      const camL = modelWorldMatrixInverse.mul(vec4(cameraPosition, 1)).xyz.toVar();
      const exit = positionLocal.toVar();                                  // on the unit sphere: the ray's far end
      const dir = normalize(exit.sub(camL)).toVar();
      const perp = exit.sub(dir.mul(dot(exit, dir))).toVar();             // the chord's midpoint
      const half = sqrt(max(float(1).sub(dot(perp, perp)), 0)).toVar();
      const entry = perp.sub(dir.mul(half)).toVar();
      // s runs along the chord from the entry point; a camera inside the sphere starts at itself
      const s0 = max(dot(camL.sub(entry), dir), 0).toVar();
      const dt = max(half.mul(2).sub(s0), 0).div(steps).toVar();
      const camT = dot(entry.sub(camL), dir).toVar();                      // the camera's distance to s = 0 (negative inside): + s = to a sample
      const ign = fract(float(52.9829189).mul(fract(dot(screenCoordinate.xy, vec2(0.06711056, 0.00583715)))));
      const jit = float(0.5).add(ign.sub(0.5).mul(U.jitter as N)).toVar();
      const acc = vec3(0).toVar(), trans = vec3(1).toVar();
      const p0 = entry.add(dir.mul(s0)).toVar();
      const fine0 = sheets.length ? fineAt(p0) : null;
      const fPrev: N[] = sheets.map((_, k) => sheetF(k, p0, fine0).toVar());
      const sStar: N[] = stars.map((_, k) => dot((starU[k].pos as N).sub(entry), dir).toVar());
      const tStar: N[] = stars.map(() => vec3(1).toVar());

      Loop(steps, ({ i }) => {
        const ss = s0.add(dt.mul(float(i as never))).toVar();              // the step's start
        const p = entry.add(dir.mul(ss.add(dt.mul(jit)))).toVar();
        let uvw: N = p.mul(0.5).add(0.5);
        let erode: N = float(1);
        if (dTex) {
          const dn = texture3D(dTex, p.mul(U.detailFreq as N)).toVar();
          uvw = uvw.add(dn.rgb.sub(0.5).mul((U.detail as N).mul(0.02)).mul(U.wisps as N));
          erode = max(float(1).add((U.detail as N).mul(dn.a.mul(2).sub(1)).mul(U.wisps as N)), 0);
        }
        const v = texture3D(volTex, uvw).toVar();
        const gas = v.r.mul(erode).toVar(), light = v.g.toVar(), dust = v.b.mul(erode).toVar();
        // where the star is from here
        let rs: N, cosT: N = float(0), inv: N = float(1);
        if (lightMode === 'point') {
          const d = p.sub(U.lightPos as N).toVar();
          rs = length(d).toVar();
          cosT = dot(d, dir).negate().div(max(rs, 1e-6));
          inv = float(1).div(rs.mul(rs).add(NEBULA_STAR_SOFT));
        } else if (lightMode === 'dir') { rs = float(1); cosT = dot(U.lightPos as N, dir); } else rs = length(p);
        const hi = pow(light, U.hiPow as N).mul(float(1).sub(smoothstep(U.hiR0 as N, U.hiR1 as N, rs))).toVar();
        const j = (U.emission as N).mul(gas).mul(gas).mul(light);
        let e: N = (U.colLow as N).mul(float(1).sub(hi)).add((U.colHigh as N).mul(hi)).mul(j).add((U.ambient as N).mul(dust));
        if (lightMode !== 'none') {
          const g = U.phaseG as N, k = max(float(1).add(g.mul(g)).sub(g.mul(2).mul(cosT)), 1e-4);
          const phase = float(1).sub(g.mul(g)).div(k.mul(sqrt(k)));
          e = e.add((U.scat as N).mul(dust.mul(pow(light, U.lightPow as N)).mul(inv).mul(phase)));
        }
        const sigma = (U.kappa as N).mul(dust).add((U.gasKappa as N).mul(gas)).toVar();
        const x = max(sigma.mul(dt), 1e-4);
        acc.addAssign(trans.mul(e).mul(dt).mul(float(1).sub(exp(x.negate())).div(x)));
        // the sheets: their profile integrated exactly across the whole step
        if (sheets.length) {
          const pe = entry.add(dir.mul(ss.add(dt))).toVar();
          const fineE = fineAt(pe);
          for (let k = 0; k < sheets.length; k++) {
            const su = sheetU[k], f1 = sheetF(k, pe, fineE).toVar(), f0 = fPrev[k];
            // the mask reads the BAKED gas (the wisps' erosion would speckle it)
            let m: N = sheets[k].maskHi > sheets[k].maskLo ? smoothstep(su.maskLo as N, su.maskHi as N, v.r) : float(1);
            if ((sheets[k].sharpen ?? 1) > 1) m = m.mul(pow(float(1).div(max(abs(f1.sub(f0)).div(max(dt, 1e-6)), NEBULA_SHEET_COS_MIN)), su.sharpen as N));
            // never thinner than a pixel's footprint here (wider and dimmer: the light is conserved, nothing sparkles)
            const w = max(su.width as N, (U.pixelAngle as N).mul(NEBULA_SHEET_PX).mul(camT.add(ss).add(dt))).toVar();
            const so = sheetInt(f0.sub(su.split as N), f1.sub(su.split as N), w, dt);
            const si = sheetInt(f0.add(su.split as N), f1.add(su.split as N), w, dt);
            acc.addAssign(trans.mul((su.colOut as N).mul(so).add((su.colIn as N).mul(si))).mul(m.div(w.mul(1.7724538509055159))));
            fPrev[k].assign(f1);
          }
        }
        trans.mulAssign(exp(sigma.mul(dt).negate()));
        for (let k = 0; k < stars.length; k++) tStar[k].assign(select(ss.add(dt).lessThanEqual(sStar[k]), trans, tStar[k]));
      });

      // the stars inside, each behind the dust in front of it only
      for (let k = 0; k < stars.length; k++) {
        const vS = (starU[k].pos as N).sub(camL).toVar();
        const d2 = dot(vS, vS), along = dot(vS, dir);
        const cr = cross(vS, dir);
        const th2 = dot(cr, cr).div(max(d2, 1e-6));
        const sg = max(float(NEBULA_STAR_SIGMA), (U.pixelAngle as N).mul(0.8));
        const sg2 = sg.mul(sg);
        const psf = exp(th2.div(sg2.mul(-2))).mul(float(NEBULA_STAR_SIGMA * NEBULA_STAR_SIGMA).div(sg2)).add(exp(th2.div(sg2.mul(-72))).mul(NEBULA_STAR_HALO));
        acc.addAssign(tStar[k].mul(starU[k].col as N).mul(psf.div(d2.add(NEBULA_STAR_NEAR))).mul(select(along.greaterThan(0), float(1), float(0))));
      }
      const a = float(1).sub(trans.x.add(trans.y).add(trans.z).div(3));
      return vec4(acc.mul(U.intensity as N).mul(U.fade as N), a.mul(U.fade as N));
    })();

    this.material = material as THREE.NodeMaterial;
    this.mesh = new THREE.Mesh(new THREE.SphereGeometry(1, 48, 32), material);
    this.mesh.name = `nebula:${spec.kind}:${spec.seed}`;
    this.mesh.renderOrder = -2;
  }

  /** Rewrite the look from a spec (same kind, light mode, sheet and star counts as the one this was built with). */
  setSpec(spec: NebulaSpec): void {
    this.spec = spec;
    const U = this.U;
    U.colLow.value.set(spec.colLow[0], spec.colLow[1], spec.colLow[2]);
    U.colHigh.value.set(spec.colHigh[0] * spec.highGain, spec.colHigh[1] * spec.highGain, spec.colHigh[2] * spec.highGain);
    U.emission.value = spec.emission; U.hiR0.value = spec.hiR0; U.hiR1.value = spec.hiR1; U.hiPow.value = spec.hiPow;
    const k = spec.light ? spec.scatter * spec.starPower : 0;
    U.scat.value.set(k * spec.starColor[0] * spec.colScatter[0], k * spec.starColor[1] * spec.colScatter[1], k * spec.starColor[2] * spec.colScatter[2]);
    U.phaseG.value = spec.phaseG; U.lightPow.value = spec.lightPow;
    U.ambient.value.set(spec.ambient[0], spec.ambient[1], spec.ambient[2]);
    U.kappa.value.set(spec.kappa * spec.redden[0], spec.kappa * spec.redden[1], spec.kappa * spec.redden[2]);
    U.gasKappa.value = spec.gasKappa;
    if (spec.light) { const p = spec.light.mode === 'dir' ? v3(spec.light.pos).normalize() : v3(spec.light.pos); U.lightPos.value.copy(p); }
    U.detail.value = spec.detail; U.detailFreq.value = spec.detailFreq;
    spec.sheets.forEach((s, i) => {
      const su = this.sheetU[i]; if (!su) return;
      su.axesInv.value.set(1 / s.axes[0], 1 / s.axes[1], 1 / s.axes[2]);
      su.radius.value = s.radius; su.amp.value = s.amp; su.width.value = s.width; su.split.value = s.split;
      const n = s.power;
      su.colOut.value.set(s.colorOut[0] * n, s.colorOut[1] * n, s.colorOut[2] * n);
      su.colIn.value.set(s.colorIn[0] * n, s.colorIn[1] * n, s.colorIn[2] * n);
      su.maskLo.value = s.maskLo; su.maskHi.value = s.maskHi; su.ripple.value = s.ripple ?? 0; su.sharpen.value = Math.max(0, (s.sharpen ?? 1) - 1);
    });
    spec.stars.forEach((s, i) => {
      const u = this.starU[i]; if (!u) return;
      u.pos.value.set(s.pos[0], s.pos[1], s.pos[2]);
      u.col.value.set(s.color[0] * s.power, s.color[1] * s.power, s.color[2] * s.power);
    });
  }

  /** Per frame: one pixel's angle (the stars' size floor). `heightPx` is the drawing buffer's height. */
  update(camera: THREE.Camera, heightPx: number): void {
    const fov = (camera as THREE.PerspectiveCamera).fov ?? 60;
    this.U.pixelAngle.value = (2 * Math.tan((fov * Math.PI) / 360)) / Math.max(1, heightPx);
  }

  /** 0..1 — fade the whole nebula (in after its bake, out before disposal). */
  setFade(v: number): void { this.U.fade.value = Math.max(0, Math.min(1, Number.isFinite(v) ? v : 1)); }
  setIntensity(v: number): void { this.U.intensity.value = Number.isFinite(v) ? Math.max(0, v) : 1; }
  /** 1 = each ray's start is dithered (the default); 0 = every ray samples mid-step (the oracle's pin). */
  setJitter(v: number): void { this.U.jitter.value = v ? 1 : 0; }
  /** The detail volume's work — the wisps and the sheets' ripples — on (1) or off (0: the march is the oracle's). */
  setWisps(on: boolean): void { this.U.wisps.value = on ? 1 : 0; }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    this.material.dispose();
    this.texture.dispose();
  }
}
